import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
} from "@mui/material";
import {
  ThumbUp,
  Comment,
  Share,
  DirectionsCar,
  Memory,
  Camera,
  PhotoCamera,
  Satellite,
  CheckCircle,
  Timeline,
  Dashboard,
  Science,
  Straighten,
  Sensors,
  Agriculture,
  Tune,
  BarChart,
  PrecisionManufacturing,
  Settings,
  School,
} from "@mui/icons-material";

import Header from "../../global_components/parameterized/Header";

// ---------------------------------------------------------------------------
// Contributor helpers
// ---------------------------------------------------------------------------
// Wandelt Rohnamen in Anzeigename + Initialen um. Unterstützte Eingabeformate:
//   "First Last"                           -> "First Last" / "FL"
//   "Last, First"                          -> "First Last" / "FL"
//   "Last, First (role, org)"              -> "First Last" / "FL"
const parseContributor = (rawName) => {
  const clean = rawName.replace(/\s*\(.*?\)\s*/g, "").trim();
  const display = clean.includes(",")
    ? clean
        .split(",")
        .map((s) => s.trim())
        .reverse()
        .join(" ")
    : clean;
  const parts = display.split(/\s+/).filter(Boolean);
  const initials =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (parts[0]?.slice(0, 2) || "?").toUpperCase();
  return { display, initials };
};

// Deterministische Farbe pro Name — derselbe Name bekommt immer dieselbe
// Farbe, egal auf welcher Karte er auftaucht. Palette ist auf das MUI-Theme
// (#11192A Navy) abgestimmt: gesättigte Mid-Tones, gut lesbar mit weißer
// Schrift.
// Aktuell nicht verwendet — Avatare nutzen die MUI-Standardfarbe (grau).
// Wenn Initialen wieder farbig sein sollen, `bgcolor: stringToColor(display)`
// im Avatar sx-Prop unten einkommentieren.
// const stringToColor = (str) => {
//   let hash = 0;
//   for (let i = 0; i < str.length; i++) {
//     hash = str.charCodeAt(i) + ((hash << 5) - hash);
//   }
//   const palette = [
//     "#1976d2",
//     "#388e3c",
//     "#c2185b",
//     "#7b1fa2",
//     "#f57c00",
//     "#0288d1",
//     "#5d4037",
//     "#00796b",
//     "#455a64",
//     "#d32f2f",
//   ];
//   return palette[Math.abs(hash) % palette.length];
// };

const SectionTitle = ({ children }) => (
  <Typography
    variant="h5"
    gutterBottom
    sx={{ fontWeight: "bold", color: "#11192A", mb: 2 }}
  >
    {children}
  </Typography>
);

const UseCaseCard = ({
  title,
  provider,
  description,
  image,
  icon: Icon,
  stats,
  space,
  category_1,
  category_2,
  category_3,
  likes,
  comments,
  features,
  timeline,
  timelineTitle,
  dashboardLink,
  progress,
  contributors,
  badge,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: 3,
      }}
    >
      {/* Image */}
      <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
        <CardMedia
          component="img"
          image={image}
          alt={title}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        {badge && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)",
              p: "10px 12px 24px",
              display: "flex",
              justifyContent: "flex-end",
              pointerEvents: "none",
            }}
          >
            <Chip
              icon={<School />}
              label={badge}
              sx={{
                backgroundColor: "#11192A",
                color: "#fff",
                fontWeight: 500,
                fontSize: "0.9rem",
                height: 36,
                borderRadius: "4px",
                px: 0.5,
                pointerEvents: "auto",
                "& .MuiChip-icon": { color: "#fff", fontSize: 20 },
                "& .MuiChip-label": { px: 1.25 },
              }}
            />
          </Box>
        )}
      </Box>

      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        {/* Title + Icon: fixed height */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 3,
            minHeight: "70px",
          }}
        >
          <Icon sx={{ fontSize: 60, color: "#11192A", mr: 2, flexShrink: 0 }} />
          <Box>
            <Typography
              variant="h3"
              color="#11192A"
              component="div"
              gutterBottom
            >
              {title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {provider}
            </Typography>
          </Box>
        </Box>

        {/* Overview: fixed height */}
        <SectionTitle>Overview</SectionTitle>
        <Box sx={{ minHeight: "60px" }}>
          <Typography variant="body1" paragraph>
            {description}
          </Typography>
        </Box>

        {/* Chips: fixed height */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 1,
            mt: 1,
            minHeight: "35px",
            alignContent: "flex-start",
          }}
        >
          <Chip label={space} size="medium" color="primary" />
          <Chip label={category_1} size="medium" variant="outlined" />
          <Chip label={category_2} size="medium" variant="outlined" />
          {category_3 && (
            <Chip label={category_3} size="medium" variant="outlined" />
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Statistics: fixed height */}
        <SectionTitle>Statistics</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2,
            mb: 1,
            minHeight: "80px",
          }}
        >
          {stats.map((stat, index) => (
            <Typography
              key={index}
              variant="body1"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {React.cloneElement(stat.icon, {
                fontSize: "medium",
                sx: { mr: 1, color: theme.palette.secondary.main },
              })}
              {stat.text}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Features: fixed height */}
        <SectionTitle>Features</SectionTitle>
        <Box sx={{ minHeight: "200px" }}>
          <List disablePadding>
            {features.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Timeline: fixed height */}
        <SectionTitle>{timelineTitle}</SectionTitle>
        <Box sx={{ width: "100%", mb: 1, mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="text.secondary" align="right">
            {progress}% Complete
          </Typography>
        </Box>
        <Box sx={{ minHeight: "230px" }}>
          <List disablePadding>
            {timeline.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <Timeline color="info" />
                </ListItemIcon>
                <ListItemText primary={item.event} secondary={item.date} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Contributors */}
        <SectionTitle>Contributors</SectionTitle>
        <Box sx={{ mb: 2, minHeight: "48px" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <AvatarGroup
              max={6}
              sx={{
                "& .MuiAvatar-root": {
                  width: 40,
                  height: 40,
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  border: "2px solid #fff",
                },
              }}
            >
              {contributors.map((contributor, index) => {
                const { display, initials } = parseContributor(
                  contributor.name,
                );
                return (
                  <Tooltip key={index} title={display} arrow>
                    <Avatar>{initials}</Avatar>
                  </Tooltip>
                );
              })}
            </AvatarGroup>
            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
              {contributors.length} contributor
              {contributors.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {contributors
              .slice(0, 5)
              .map((c) => parseContributor(c.name).display)
              .join(", ")}
            {contributors.length > 5 && ` +${contributors.length - 5} weitere`}
          </Typography>
        </Box>

        {/*
          Like / Comment / Share — DEAKTIVIERT.
          Wird aktuell nicht angezeigt, da noch nicht implementiert:
            - keine Backend-Endpunkte für Likes/Comments/Share
            - keine User-Auth / kein Like-State pro User
            - IconButtons hätten keinen onClick-Handler
            - {likes} und {comments} in useCases[] sind Dummy-Werte

          Wenn wieder aktivieren:
            1. Diesen JSX-Block einkommentieren
            2. Backend-Endpunkte anbinden (POST /api/usecases/:id/like,
               GET /api/usecases/:id/comments, o. ä.)
            3. useState/React-Query für Like-Status + onClick-Handler mit
               optimistic UI update
            4. Comment-Modal oder eigene Route
            5. Share-Button: Web Share API oder Copy-Link-Fallback

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: "auto",
          }}
        >
          <IconButton size="large">
            <ThumbUp />
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {likes}
          </Typography>
          <IconButton size="large">
            <Comment />
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {comments}
          </Typography>
          <IconButton size="large">
            <Share />
          </IconButton>
        </Box>
        */}

        <Button
          variant="contained"
          startIcon={<Dashboard />}
          fullWidth
          size="large"
          sx={{ mt: "auto", pt: 1.5, pb: 1.5, backgroundColor: "#11192A" }}
          href={dashboardLink}
        >
          View Dashboard
        </Button>
      </CardContent>
    </Card>
  );
};

const HomePage = () => {
  const useCases = [
    {
      title: "Field Test Vehicle",
      provider:
        "Institute of Product Development, Gottfried Wilhelm Leibniz Universität Hannover",
      description:
        "Our test vehicle integrates technologies to FAIRifies data collection and analysis in automotive environments. This system combines cameras, LiDAR, GPS, and IMU sensors to provide insights into vehicle performance, road conditions, and driving behaviors.",
      image: "../../homepage_assets/passat.jpeg",
      icon: DirectionsCar,
      stats: [
        { icon: <DirectionsCar />, text: "7 Drives" },
        { icon: <Camera />, text: "22 Recordings" },
        { icon: <PhotoCamera />, text: "3580 Images" },
        { icon: <Satellite />, text: "245 LiDAR Scans" },
      ],
      space: "Automotive",
      category_1: "Field Data",
      category_2: "Dataset Preview",
      category_3: "Digital Twin",
      // TODO: Dummy-Werte — noch nicht ans Backend angebunden
      likes: 21,
      comments: 5,
      features: [
        "360° environmental mapping",
        "Field Data Analysis Framework for Previsualization Before Downloading",
        "AI-powered Object Detection and Classification as a Derived Data Generation from Raw Data (Coming soon!)",
        "AI-Powered Dataset Suggestion (Coming soon!)",
      ],
      timeline: [
        { event: "Project Initiation", date: "September 2020" },
        { event: "Prototype Development", date: "March 2023" },
        { event: "Field Data Collection", date: "June 2023" },
        { event: "Field Database Platform", date: "March 2024" },
      ],
      timelineTitle: "Project Timeline",
      dashboardLink: "/luh/dashboard",
      progress: 70,
      contributors: [
        { name: "Rayen Hamlaoui", avatar: "https://example.com/avatar4.jpg" },
        {
          name: "Atefeh Gooran-Orimi",
          avatar: "https://example.com/avatar5.jpg",
        },
      ],
    },
    {
      title: "RoBivaL",
      provider:
        "DFKI Robotics Innovation Center, Bremen - HSO Agro-Technicum, Osnabrück",
      description:
        "The RoBivaL project compared robot locomotion concepts from space research and agriculture. Four robots were tested during six experiments addressing different agricultural challenges. Soil conditions were varied regarding moisture and density.",
      image: "../../homepage_assets/robival.png",
      icon: Agriculture,
      stats: [
        { icon: <Science />, text: "6 Experiments" },
        { icon: <Straighten />, text: "30 Parameters" },
        { icon: <CheckCircle />, text: "231 Experiment runs" },
        { icon: <Sensors />, text: "12 Sensor data classes" },
      ],
      space: "Robotics",
      category_1: "Agriculture",
      category_2: "Locomotion",
      category_3: "Field Data",
      // TODO: Dummy-Werte — noch nicht ans Backend angebunden
      likes: 21,
      comments: 5,
      features: [
        "Multimodal sensor data for system and field monitoring",
        "IMU, RTK-GPS, Video camera, Tilt laser scanner, Force logger, Penetrometer",
        "Quantitative documentation of field conditions and systems performance",
        "Specifications of experiments, parameters, systems, and data types",
      ],
      timeline: [
        { event: "Project Start", date: "August 2021" },
        { event: "Field Data Collection", date: "April / June 2023" },
        { event: "Project End", date: "October 2023" },
        { event: "Field Database Platform", date: "March 2024" },
      ],
      timelineTitle: "Project Timeline",
      dashboardLink: "/dfki/robival/dashboard",
      progress: 100,
      contributors: [
        {
          name: "Backe, Christian (Data manager, DFKI)",
          avatar: "https://example.com/avatar4.jpg",
        },
        {
          name: "Wirkus, Malte (Project leader, DFKI)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Hinck, Stefan (Project member, HSO)",
          avatar: "https://example.com/avatar4.jpg",
        },
        {
          name: "Babel, Jonathan (Project member, DFKI)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Riedel, Vadim (Project member, HSO)",
          avatar: "https://example.com/avatar4.jpg",
        },
        {
          name: "Reichert, Nele (Project member, DFKI)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Kolesnikov, Andrej (Project member, DFKI)",
          avatar: "https://example.com/avatar4.jpg",
        },
        {
          name: "Stark, Tobias (Project member, DFKI)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Hilljegerdes, Jens (Project member, DFKI)",
          avatar: "https://example.com/avatar4.jpg",
        },
        {
          name: "Kücüker, Hilmi Dogu (Project member, DFKI)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Barcic, Emir (Project member, HSO)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Klink, Eduard (Project member, HSO)",
          avatar: "https://example.com/avatar4.jpg",
        },
        {
          name: "Ruckelshausen, Arno (Supervisor, HSO)",
          avatar: "https://example.com/avatar5.jpg",
        },
        {
          name: "Kirchner, Frank (Supervisor, DFKI)",
          avatar: "https://example.com/avatar5.jpg",
        },
      ],
    },
    {
      title: "Drill Process Timeseries (simulated)",
      provider: "WZL of RWTH Aachen University",
      description:
        "A simulated dataset for training purposes, showing features during a drilling process. The dataset covers thermal behaviour, load and demand, as well as energy and wear signals across two different machines with varying operational conditions.",
      image: "../../homepage_assets/Drill_Process.png",
      icon: Memory,
      stats: [
        { icon: <Tune />, text: "6 Data features" },
        { icon: <BarChart />, text: "> 10,000 Measurement values" },
        { icon: <PrecisionManufacturing />, text: "2 Machines" },
        { icon: <Settings />, text: "26 Simulation parameters" },
      ],
      space: "Manufacturing",
      category_1: "Simulated Data",
      category_2: "Digital Shadow",
      // TODO: Dummy-Werte — noch nicht ans Backend angebunden
      likes: 21,
      comments: 5,
      features: [
        "Controllable data creation for training purpose",
        "Tabular features plus failure types",
        "Timestamps, two different machines, and 4 signals (thermal behaviour, load and demand, and energy and wear)",
        "Consistent data format across both machines",
      ],
      timeline: [
        { event: "Data Collection Start", date: "12 Februar 2026" },
        { event: "Data Collection End", date: "16 Februar 2026" },
        { event: "Dataset Purpose", date: "Training (NFDI4ING RDM Basics)" },
        { event: "Field Database Platform Publicaiton", date: "Mai 2026" },
      ],
      timelineTitle: "Dataset Timeline",
      dashboardLink: "/rwth/drilloverview",
      progress: 100,
      badge: "For training purpose",
      contributors: [
        { name: "Hans Zhou", avatar: "https://example.com/avatar1.jpg" },
        { name: "Sven Münker", avatar: "https://example.com/avatar2.jpg" },
        { name: "Tobias Hamann", avatar: "https://example.com/avatar3.jpg" },
        { name: "Mario Moser", avatar: "https://example.com/avatar4.jpg" },
      ],
    },
  ];

  return (
    <Box m="20px">
      <Header title="Field Use-Cases" />
      <Grid container spacing={4} alignItems="stretch">
        {useCases.map((useCase, index) => (
          <Grid item xs={12} lg={4} key={index} sx={{ display: "flex" }}>
            <UseCaseCard {...useCase} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HomePage;
